import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    PLATFORM_ID,
    signal,
    ViewChild,
} from '@angular/core';
import { MAIN_MENU_ITEMS, MenuType, STATUS_TOPIC } from '../../../constants/menu.constant';
import { filter, firstValueFrom, forkJoin, skip, Subject, takeUntil } from 'rxjs';
import { ErrorService } from '../../../services/error.service';
import { RecursiveLinkInterface, RecursiveLinkParamInterface, RecursiveLinksModel } from '../menu.model';
import { ProgramFilterInterface } from '../../../models/filter.model';
import { ProgramFilterService } from '../../webinar-course/program-filter.service';
import { AccountService } from '../../../../features/authentication/services/account.service';
import { UserDetailsInterface } from '../../../models/user.model';
import { NavigationEnd, Router } from '@angular/router';
import { StorageService } from '../../../services/storage.service';
import { Language } from '../../../constants/ui-utils.constant';
import { DealsService } from '../../../services/api/deals.service';
import { FaqService } from '../../../services/api/faq.service';
import { isPlatformBrowser } from '@angular/common';
import { CustomToastrService } from '../../../services/custom-toastr.service';
import { MultilingualService } from '../../../services/api/multilingual.service';

@Component({
    selector: 'app-navbar',
    templateUrl: './navbar.component.html',
    styleUrl: './navbar.component.scss',
})
export class NavbarComponent implements OnInit, OnDestroy {
    @ViewChild('dropdownMenu') dropdownMenu!: ElementRef;
    @Input() isMobile = false;
    @Output() closeMobileMenu = new EventEmitter();

    protected isLoading = false;
    protected isClientSide = signal(false);
    protected userDetails: UserDetailsInterface | null = null;
    protected menu: RecursiveLinkInterface[] = MAIN_MENU_ITEMS;
    protected subMenuData: RecursiveLinkInterface[] = [];
    protected selectedMainMenuType!: MenuType | null;
    protected subMenuFirstLevelSelectedId = '';
    protected subMenuSecondLevelSelectedId = '';

    private hasFaqsAvailable = true;
    private destroy$ = new Subject<void>();

    protected SubMenuLevel = SubMenuLevel;
    protected MenuType = MenuType;

    constructor(
        private accountService: AccountService,
        private errorService: ErrorService,
        private programFilterService: ProgramFilterService,
        private router: Router,
        private storageService: StorageService,
        private dealsService: DealsService,
        private faqService: FaqService,
        private customToastrService: CustomToastrService,
        private multilingualService: MultilingualService,
        @Inject(PLATFORM_ID) private platformId: object
    ) {
        // Consolidated client-side initialization.
        // Developer Note: We cannot utilize "afterNextRender"
        // for this segment as the API calls triggered after the first
        // render would invoke NG0100 error.
        // For more info, refer to this link:
        // https://angular.dev/errors/NG0100
        this.isClientSide.set(isPlatformBrowser(this.platformId));
    }

    async ngOnInit(): Promise<void> {
        if (!this.isClientSide())
            return;

        // Developer Note: Moved away from using "afterNextRender" after 
        // previous encounter with [NG0100: ExpressionChangedAfterItHasBeenCheckedError].
        this.isLoading = true;

        try {
            await this.getUserDetails();
            if (!this.userDetails) {
                this.isLoading = false;
                return;
            }

            // Trigger fetchTopics if not already loaded (e.g., when invalid-url page bypasses admin layout)
            this.programFilterService.fetchTopics();

            await this.buildMenu();
            this.closeMenuOnRouteChange();

            // TPOD-2263: rebuild the menu reactively on language change so topic
            // submenu labels (API content, not i18n pipe strings) stay in sync
            // without the old full-page reload.
            this.subscribeToLanguageChange();
        }
        catch (error) {
            this.errorService.showError(error);
        }
        finally {
            this.isLoading = false;
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private async buildMenu(): Promise<void> {
        // Reset to a fresh copy of the static base first: initMenu appends
        // children and the deals/FAQ checks filter items, so re-running the
        // build against an already-built menu would compound. MAIN_MENU_ITEMS
        // is plain data, so structuredClone is safe.
        this.menu = structuredClone(MAIN_MENU_ITEMS);

        await this.getTopicsFilter();
        await this.checkDealsAvailability();
        await this.checkFaqAvailability();
    }

    private subscribeToLanguageChange(): void {
        // currentLanguage is a BehaviorSubject — skip its current value (the menu
        // was just built in that language) and only rebuild on actual changes.
        this.multilingualService.currentLanguage.pipe(skip(1), takeUntil(this.destroy$)).subscribe(() => {
            this.buildMenu().catch((error) => this.errorService.showError(error));
        });
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        if (this.dropdownMenu) {
            if (!this.dropdownMenu.nativeElement.contains(event.target)) {
                this.selectedMainMenuType = null;
            }
        }
    }

    protected onLinkClick(param?: RecursiveLinkParamInterface): void {
        if (param?.topic || Number.isInteger(param?.programType) || Number.isInteger(param?.webinarType)) {
            this.programFilterService.filterNavbarDataSubject.next(param!);
        }

        this.selectedMainMenuType = null;
        this.subMenuFirstLevelSelectedId = '';
        this.subMenuSecondLevelSelectedId = '';
        this.closeMobileMenu.emit();
    }

    protected toggleMenu(menuType?: MenuType, toggle = false): void {
        if (!toggle) {
            this.subMenuData = [];
            this.selectedMainMenuType = null;
            this.subMenuSecondLevelSelectedId = '';

            return;
        }

        if (menuType !== undefined) {
            this.selectedMainMenuType = menuType;
        }
    }

    protected onSubMenuSelected(id?: string, level?: SubMenuLevel): void {
        if (!id) {
            return;
        }

        switch (level) {
            case SubMenuLevel.FirstLevel:
                this.subMenuFirstLevelSelectedId = id;
                break;
            case SubMenuLevel.SecondLevel:
                this.subMenuSecondLevelSelectedId = id;
                break;
        }

        this.menu.forEach((menu) => {
            menu.children?.find((child) => {
                if (child.id === id) {
                    this.subMenuData = child.children!;
                }
            });
        });
    }

    private closeMenuOnRouteChange(): void {
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.subMenuData = [];
                this.subMenuSecondLevelSelectedId = '';
                this.selectedMainMenuType = null;
            }
        });
    }

    private async getUserDetails(): Promise<void> {
        try {
            const resp = await firstValueFrom(this.accountService.getUserDetails());
            if (resp) {
                this.userDetails = resp;
            }
        }
        catch (error) {
            // Error will be handled by ngOnInit's catch block
            throw error;
        }
    }

    private async getTopicsFilter(): Promise<void> {
        try {
            const response = await firstValueFrom(
                this.programFilterService.topics$
                    .pipe(filter((response) => response && Object.keys(response).length > 0))
            );

            this.initMenu(MenuType.Webinars, response);
            //this.assignEInvoiceToMenu(); //--- not using
            this.assignTopicsToMenu(response, MenuType.UpdateClasses, 'updateClasses', '/program/list/update-classes',);
            this.assignTopicsToMenu(response, MenuType.EInvoicing, 'eInvoicing', '/program/list/e-invoicing');
            this.assignTopicsToMenu(response, MenuType.IncomeTax, 'incomeTax', '/program/list/income-tax');
            this.assignTopicsToMenu(response, MenuType.Forms, 'forms', '/program/list/forms');
            this.assignTopicsToMenu(response, MenuType.SST, 'sst', '/program/list/sst');
        }
        catch (error) {
            // Error will be handled by ngOnInit's catch block
            throw error;
        }
    }

    private assignEInvoiceToMenu(): void {
        this.programFilterService.eInvoicingId$.subscribe({
            next: (eInvoicingId) => {
                if (!eInvoicingId) {
                    return;
                }

                this.menu = this.menu.map((menu) => {
                    if (menu.menuType === MenuType.EInvoicing) {
                        return {
                            ...menu,
                            link: '/program/list/e-invoicing',
                            param: { topic: eInvoicingId },
                        };
                    }
                    return menu;
                });
            },
        });
    }

    private assignTopicsToMenu(
        responseData: ProgramFilterInterface,
        menuType: MenuType,
        statusTopicKey: keyof typeof STATUS_TOPIC,
        baseLink: string,
    ): void {
        const topicData = responseData.programTopics
            .filter((topic) => topic.name.toLowerCase() === STATUS_TOPIC[statusTopicKey].toLowerCase())
            .map((topic) => new RecursiveLinksModel(topic, this.storageService.language));

        if (!topicData.length) {
            return;
        }

        this.menu = this.menu.map((menu) => {
            if (menu.menuType === menuType) {
                const hasChildren = topicData[0].children && topicData[0].children.length > 0;

                let menuParams: RecursiveLinkParamInterface = { topic: topicData[0].id };
                let childParams: RecursiveLinkParamInterface = {};

                switch (menuType) {
                    case MenuType.EInvoicing:
                    case MenuType.IncomeTax:
                    case MenuType.Forms:
                    case MenuType.SST:
                        //menuParams.programType = childParams.programType = ProgramType.Course;
                        break;
                }

                let tempChildren:RecursiveLinksModel[] = [];

                if(hasChildren){
                    tempChildren = this.filterChildrenData(topicData[0].children);
                }

                return {
                    ...menu,
                    link: baseLink,
                    param: menuParams,
                    ...(hasChildren && {
                        children: tempChildren.map((child) => ({
                            ...child,
                            link: baseLink,
                            param: { ...child.param, ...childParams }
                        })),
                    }),
                };
            }
            return menu;
        });
    }

    private filterChildrenData(children?: RecursiveLinksModel[]): any {

        let byPassCategory: string[] = [];

        let tempChildren = children;
        
        let tempFilter = tempChildren?.filter(f => {
            let tempTranslation = '';

            let languageFilter = f.model.translations.filter(t => {
                return t.language == Language.EN;
            })

            tempTranslation = (languageFilter[0] && languageFilter[0].description) ? languageFilter[0].description.toLowerCase() : f.label.toLowerCase();

            return !byPassCategory.includes(tempTranslation);
        })

        return tempFilter;
    }

    private async checkDealsAvailability(): Promise<void> {
        try {
            const response = await firstValueFrom(this.dealsService.getDealsCount());
            if (response.result.total === 0) {
                // Remove Deals menu item if no deals available
                this.menu = this.menu.filter(
                    menu => menu.menuType !== MenuType.Deals
                );
            }
        }
        catch (err) {
            // On error, hide the deals menu for safety
            this.menu = this.menu.filter(
                menu => menu.menuType !== MenuType.Deals
            );

            // Display error toast message
            this.customToastrService.showError('Unable to load deals information. Please try again later.');

            // Debug Log (Error)
            // console.error('Error checking deals availability:', err);
        }
    }

    private async checkFaqAvailability(): Promise<void> {
        try {
            const response = await firstValueFrom(this.faqService.getFaqCount());
            this.hasFaqsAvailable = response.total > 0;

            if (response.total === 0) {
                // Remove FAQ sub-menu item if no FAQs available
                this.menu = this.menu.map(menu => {
                    if (menu.menuType === MenuType.Resources && menu.children) {
                        return {
                            ...menu,
                            children: menu.children.filter(
                                child => child.menuType !== MenuType.FAQ
                            )
                        };
                    }
                    return menu;
                });
            }
        }
        catch (err) {
            // On error, hide the FAQ sub-menu for safety and mark as unavailable
            this.hasFaqsAvailable = false;
            this.menu = this.menu.map(menu => {
                if (menu.menuType === MenuType.Resources && menu.children) {
                    return {
                        ...menu,
                        children: menu.children.filter(
                            child => child.menuType !== MenuType.FAQ
                        )
                    };
                }
                return menu;
            });

            // Display error toast message
            this.customToastrService.showError('Unable to load FAQ information. Please try again later.');

            // Debug Log (Error)
            // console.error('[Navigation Bar] Error checking FAQ availability:', err);
        }
    }

    private initMenu(menuType: MenuType, responseData: ProgramFilterInterface): void {
        switch (menuType) {
            case MenuType.Courses:
                this.menu = this.menu.map((menu) => {
                    if (menu.menuType === MenuType.Courses && menu.children) {
                        const newChildren = responseData.programTopics
                            .filter(
                                (topic) =>
                                    topic.name.toLowerCase() !== STATUS_TOPIC.updateClasses.toLowerCase() &&
                                    topic.name.toLowerCase() !== STATUS_TOPIC.eInvoicing.toLowerCase(),
                            )
                            .map((topic) => new RecursiveLinksModel(topic, this.storageService.language));

                        return {
                            ...menu,
                            children: [...menu.children, ...newChildren],
                        };
                    }

                    return menu;
                });

                break;
            case MenuType.Webinars:
                this.menu = this.menu.map((menu) => {
                    if (menu.menuType === MenuType.Webinars && menu.children) {
                        return {
                            ...menu,
                            children: [...menu.children],
                        };
                    }

                    return menu;
                });

                break;
        }
    }
}

enum SubMenuLevel {
    FirstLevel,
    SecondLevel,
}
